// A CM's site is made with it, named after it (server/src/mutations/cm-sites.ts; Mitch, 2026-11-04: "If we indicate
// that a vendor is a CM, why can't it just automatically set up the site for them"). What's left for admins: a Shopify
// location that is a CM's (Tailhoo Technology, Elecrow), linked to it so its Shopify stock comes in; it takes the place
// of the CM's automatic site while nothing has happened there.
import { useMemo, useState } from "react";
import { COUNT_POLICIES, COUNT_POLICY_LABELS, type CountPolicy } from "../../../../shared/consignment.ts";
import { mutateWithResult } from "../../lib/mutate.ts";
import { setSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Button, Select } from "../../ui/controls.tsx";
import { toast } from "../../ui/toast.tsx";
import { Modal } from "./parts.tsx";

/** Shopify locations (or a vendor's own site) that could be a CM's: the Link a Shopify location dialog lists them. */
export const cmCandidates = <L extends { is_cm: boolean; kind: string; shopify_location_id: string | null }>(locations: L[]) =>
  locations.filter((l) => !l.is_cm && (l.kind === "vendor_site" || (l.kind === "hhl" && l.shopify_location_id)));

export function SetupCmSite({ preset, onClose }: { preset?: string; onClose: () => void }) {
  const locations = useRows("location");
  const vendors = useRows("vendor");
  const candidates = useMemo(
    () => cmCandidates(locations).sort((a, b) => Number(!a.ignored) - Number(!b.ignored) || a.name.localeCompare(b.name)),
    [locations],
  );
  const [locationId, setLocationId] = useState(preset && candidates.some((c) => c.id === preset) ? preset : (candidates[0]?.id ?? ""));
  const chosen = candidates.find((c) => c.id === locationId);
  const cms = useMemo(
    () =>
      vendors
        .filter((v) => !v.archived)
        .sort((a, b) => Number(a.kind === "supplier") - Number(b.kind === "supplier") || (a.short_name || a.name).localeCompare(b.short_name || b.name)),
    [vendors],
  );
  // A guess from the location's name (Tailhoo Technology → Tailhoo), else its vendor, else the first CM.
  const guess = (id: string) => {
    const loc = candidates.find((c) => c.id === id);
    if (loc?.vendor_id) return loc.vendor_id;
    const words = (loc?.name ?? "")
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 3);
    return cms.find((v) => words.some((w) => `${v.name} ${v.short_name}`.toLowerCase().includes(w)))?.id ?? cms.find((v) => v.kind !== "supplier")?.id ?? "";
  };
  const [vendorId, setVendorId] = useState(() => guess(locationId));
  const [policy, setPolicy] = useState<CountPolicy>("monthly");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!locationId) return toast.error("Choose the location");
    if (!vendorId) return toast.error("Choose the vendor");
    setBusy(true);
    const { ok, result } = await mutateWithResult("location.make_cm_site", { id: locationId, vendor_id: vendorId, count_policy: policy });
    setBusy(false);
    if (!ok) return;
    const r = result as { id: string; shopify: boolean };
    if (r.shopify) {
      // Bring its Shopify stock in now rather than at the next 6-hourly pull (best effort).
      fetch("/api/shopify/run", {
        method: "POST",
        headers: { "content-type": "application/json", "x-requested-with": "hhl-ops" },
        body: JSON.stringify({ job: "stock" }),
      }).catch(() => {});
    }
    toast.ok(r.shopify ? "Linked; its Shopify stock is being pulled" : "Linked");
    setSearch({ cm: r.id, setup: null });
    onClose();
  };

  return (
    <Modal
      title="Link a Shopify location to a CM"
      onClose={onClose}
      width={520}
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || !locationId} onClick={submit}>
            Link it
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 p-4">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Location</span>
          <Select
            value={locationId}
            onChange={(e) => {
              setLocationId(e.target.value);
              setVendorId(guess(e.target.value));
            }}
            options={candidates.map((l) => ({
              value: l.id,
              label: `${l.name}${l.shopify_location_id ? (l.ignored ? " (Shopify, not used)" : " (Shopify)") : " (vendor site)"}`,
            }))}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Contract manufacturer</span>
          <Select
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            options={[{ value: "", label: "Choose a vendor…" }, ...cms.map((v) => ({ value: v.id, label: v.short_name || v.name }))]}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Ask for a count</span>
          <Select
            value={policy}
            onChange={(e) => setPolicy(e.target.value as CountPolicy)}
            options={COUNT_POLICIES.map((p) => ({ value: p, label: COUNT_POLICY_LABELS[p] }))}
          />
        </label>
        <div className="text-[12px] text-muted">
          {chosen?.shopify_location_id
            ? `${chosen.name} is a Shopify location: Shopify items' stock there keeps coming from Shopify (only Shopify changes it), and HHL Ops' own parts there are kept in its ledger. It replaces the CM's own site while nothing has happened there.`
            : "Every CM has a site already, made when it became a CM and named after it: link a Shopify location only when the CM is one in Shopify."}
        </div>
      </div>
    </Modal>
  );
}
